<script setup>
import { computed, watch, ref, provide } from 'vue'
import { usePage } from '@inertiajs/vue3'
import ToastNotification from '@/components/ToastNotification.vue'
import { useToast } from '@/composables/useToast'

const page = usePage()

const client   = computed(() => page.props.client  || {})
const auth     = computed(() => page.props.auth    || {})
const modules  = computed(() => page.props.modules || {})
const flash    = computed(() => page.props.flash   || {})

const currentComponent = computed(() => page.component || '')

const collapsed = ref(localStorage.getItem('sidebar_collapsed') === 'true')

function toggleSidebar() {
  collapsed.value = !collapsed.value
  localStorage.setItem('sidebar_collapsed', collapsed.value)
}

provide('sidebarCollapsed', collapsed)

const navItems = computed(() => [
  {
    label: 'Projets',
    icon:  'construction',
    href:  '/projets',
    active: currentComponent.value.startsWith('Projets/'),
  },
  {
    label: 'Rapports',
    icon:  'analytics',
    href:  '/rapports/snew',
    active: currentComponent.value.startsWith('Rapports/'),
  },
  modules.value.equipement && {
    label: 'Équipements',
    icon:  'handyman',
    href:  '/equipements',
    active: currentComponent.value.startsWith('Equipements/') || currentComponent.value.startsWith('EquipementImports/'),
  },
  modules.value.personnel && {
    label: 'Personnel',
    icon:  'group',
    href:  '/personnels',
    active: currentComponent.value.startsWith('Personnels/') || currentComponent.value.startsWith('PersonnelImports/'),
  },
  modules.value.inventaire && {
    label: 'Inventaire',
    icon:  'inventory_2',
    href:  '/inventaires',
    active: currentComponent.value.startsWith('Inventaires/'),
  },
  // Bons — accès via la page Projet uniquement, pas dans le menu principal
  // modules.value.bon && {
  //   label: 'Bons',
  //   icon:  'receipt_long',
  //   href:  '/bons',
  //   active: currentComponent.value.startsWith('Bons/'),
  // },
  auth.value?.is_administrateur && {
    label: 'Configuration',
    icon:  'settings',
    href:  '/administration',
    active: currentComponent.value.startsWith('Administration/')
         || currentComponent.value.startsWith('Clients/'),
  },
  auth.value?.user?.can_edit_users && {
    label: 'Utilisateurs',
    icon:  'manage_accounts',
    href:  '/utilisateurs',
    active: currentComponent.value.startsWith('Utilisateurs/'),
  },
].filter(Boolean))

const { success, error: toastError } = useToast()

watch(flash, (f) => {
  if (f?.notice) success(f.notice)
  if (f?.alert)  toastError(f.alert)
}, { immediate: true })

function signOut() {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = '/utilisateurs/sign_out'
  const m = document.createElement('input')
  m.type = 'hidden'; m.name = '_method'; m.value = 'delete'
  form.appendChild(m)
  const t = document.createElement('input')
  t.type = 'hidden'
  t.name  = document.querySelector('meta[name="csrf-param"]')?.content
  t.value = document.querySelector('meta[name="csrf-token"]')?.content
  form.appendChild(t)
  document.body.appendChild(form)
  form.submit()
}
</script>

<template>
  <div class="flex h-screen bg-surface text-on-surface font-body overflow-hidden">

    <!-- Sidebar -->
    <aside
      :class="collapsed ? 'w-16' : 'w-64'"
      class="flex-shrink-0 flex flex-col py-8 bg-surface border-r border-outline-variant/10 overflow-y-auto overflow-x-hidden custom-scrollbar transition-all duration-300"
    >

      <!-- Brand -->
      <div :class="collapsed ? 'px-0 justify-center' : 'px-4'" class="mb-10 flex items-center gap-3">
        <div :class="collapsed ? 'justify-center w-full' : ''" class="flex items-center gap-3">
          <img
            v-if="client.logo"
            :src="client.logo_is_url ? client.logo : `/images/client/${client.logo}`"
            alt="Logo"
            class="h-7 w-auto object-contain flex-shrink-0"
          />
          <span v-else class="material-symbols-outlined text-primary text-2xl flex-shrink-0">shield</span>
          <div v-if="!collapsed" class="min-w-0">
            <div class="text-base font-black text-primary tracking-tighter uppercase leading-tight truncate">
              {{ client.nom || 'auRapport' }}
            </div>
            <div class="text-[10px] font-medium tracking-widest text-outline uppercase">
              Construction Management
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 space-y-1" :class="collapsed ? 'px-2' : 'px-4'">
        <a
          v-for="item in navItems"
          :key="item.href"
          :href="item.href"
          :title="collapsed ? item.label : ''"
          :class="[
            'flex items-center transition-all duration-150 rounded-sm',
            collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3',
            item.active
              ? 'text-primary border-l-4 border-tertiary bg-surface-container font-bold'
              : 'text-outline hover:text-on-surface hover:bg-surface-container/50 border-l-4 border-transparent'
          ]"
        >
          <span class="material-symbols-outlined text-xl flex-shrink-0">{{ item.icon }}</span>
          <span v-if="!collapsed" class="text-sm tracking-wide">{{ item.label }}</span>
        </a>
      </nav>

      <!-- Toggle button -->
      <div :class="collapsed ? 'px-2' : 'px-4'" class="mt-4 mb-2">
        <button
          @click="toggleSidebar"
          :title="collapsed ? 'Étendre le menu' : 'Réduire le menu'"
          :class="collapsed ? 'justify-center w-full' : 'w-full px-4'"
          class="flex items-center gap-2 py-2 text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all duration-150"
        >
          <span class="material-symbols-outlined text-xl flex-shrink-0">
            {{ collapsed ? 'chevron_right' : 'chevron_left' }}
          </span>
          <span v-if="!collapsed" class="text-xs font-black uppercase tracking-widest">Réduire</span>
        </button>
      </div>

      <!-- User -->
      <div :class="collapsed ? 'px-2' : 'px-4'" class="pt-4 border-t border-outline-variant/15">
        <div :class="collapsed ? 'justify-center' : 'justify-between'" class="flex items-center gap-3">
          <div
            :title="collapsed ? (auth.user?.nom_complet || '') : ''"
            class="w-8 h-8 rounded-lg bg-primary-container flex items-center justify-center flex-shrink-0"
          >
            <span class="material-symbols-outlined text-sm text-on-primary">person</span>
          </div>
          <div v-if="!collapsed" class="min-w-0 flex-1">
            <div class="text-sm font-bold text-on-surface truncate">
              {{ auth.user?.nom_complet || '—' }}
            </div>
          </div>
          <button
            v-if="!collapsed"
            class="text-outline hover:text-error transition-colors flex-shrink-0"
            title="Déconnexion"
            @click="signOut"
          >
            <span class="material-symbols-outlined text-xl">logout</span>
          </button>
          <button
            v-else
            class="text-outline hover:text-error transition-colors"
            title="Déconnexion"
            @click="signOut"
          >
            <span class="material-symbols-outlined text-xl">logout</span>
          </button>
        </div>
      </div>
    </aside>

    <!-- Main -->
    <div class="flex-1 flex flex-col min-w-0 overflow-hidden">

      <!-- TopBar -->
      <header class="h-16 flex-shrink-0 flex items-center justify-between px-8 bg-surface/60 backdrop-blur-md border-b border-outline-variant/10 z-10">
        <slot name="topbar-left">
          <div class="text-sm text-outline">{{ client.nom }}</div>
        </slot>
        <div class="flex items-center gap-4">
          <slot name="topbar-right" />
        </div>
      </header>

      <ToastNotification />

      <!-- Content -->
      <main class="flex-1 overflow-y-auto custom-scrollbar bg-surface">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: #0d1322; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #424754; border-radius: 10px; }
</style>
